import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Trash2, Eye, EyeOff, LogOut, Star, ArrowLeft, Leaf, MessageCircle, TrendingUp, Download, Mail } from "lucide-react";
import { errMsg } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "beb_admin_token";

function auth() {
  const t = localStorage.getItem(TOKEN_KEY);
  return { headers: { Authorization: `Bearer ${t}` } };
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ivory px-5">
      <form onSubmit={submit} className="w-full max-w-sm bg-linen border border-border rounded-[1.4rem] p-8" data-testid="admin-login">
        <div className="flex items-center gap-2 text-terracotta mb-6"><Leaf size={20} /><span className="font-mono-tech text-xs tracking-widest uppercase">Private · Admin</span></div>
        <h1 className="font-display text-3xl text-caramel font-light mb-6">Welcome back, Barbara.</h1>
        <label className="block mb-4">
          <span className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className="mt-1.5 w-full rounded-xl border border-border bg-ivory px-4 py-3 text-caramel outline-none focus:border-terracotta" />
        </label>
        <label className="block mb-6">
          <span className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted">Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className="mt-1.5 w-full rounded-xl border border-border bg-ivory px-4 py-3 text-caramel outline-none focus:border-terracotta" />
        </label>
        <button disabled={busy} data-testid="admin-login-submit" className="w-full rounded-full bg-terracotta text-ivory py-3 font-medium hover:bg-warmdark transition-colors disabled:opacity-60">{busy ? "Signing in..." : "Sign in"}</button>
        <a href="/" className="mt-4 inline-flex items-center gap-1.5 text-sm text-warmmuted hover:text-terracotta"><ArrowLeft size={15} /> Back to site</a>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [items, setItems] = useState([]);
  const [avail, setAvail] = useState({ open: true, message: "Open to new roles" });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [t, a, an] = await Promise.all([
        axios.get(`${API}/admin/testimonials`, auth()),
        axios.get(`${API}/availability`),
        axios.get(`${API}/admin/ask-analytics`, auth()).catch(() => ({ data: null })),
      ]);
      setItems(t.data); setAvail(a.data); setAnalytics(an.data);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const setApproval = async (id, approved) => {
    try {
      await axios.patch(`${API}/admin/testimonials/${id}`, { approved }, auth());
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, approved } : i)));
      toast.success(approved ? "Published to the site" : "Hidden from the site");
    } catch { toast.error("Could not update"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, auth());
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
  };
  const saveAvail = async (next) => {
    setAvail(next);
    try { await axios.put(`${API}/admin/availability`, next, auth()); toast.success("Availability updated"); }
    catch { toast.error("Could not save"); }
  };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  const [renotifying, setRenotifying] = useState(false);
  const renotifyPending = async () => {
    const pending = items.filter((i) => !i.approved).length;
    if (!pending) return toast.info("No pending testimonials to email.");
    if (!window.confirm(`Send ${pending} email${pending===1?"":"s"} to your inbox with approve/reject buttons?`)) return;
    setRenotifying(true);
    try {
      const { data } = await axios.post(`${API}/admin/testimonials/renotify`, {}, auth());
      toast.success(`Sent ${data.emails_sent} email${data.emails_sent===1?"":"s"} to your inbox.`);
    } catch (e) {
      toast.error(errMsg(e, "Could not send the notification emails."));
    } finally { setRenotifying(false); }
  };

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-border bg-linen/60">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div>
            <div className="font-display text-2xl text-caramel leading-none">Testimonial Studio</div>
            <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted">Barbara E. Betancourt · Admin</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-warmmuted hover:text-terracotta">View site</a>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-caramel hover:border-terracotta hover:text-terracotta transition-colors text-sm"><LogOut size={15} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-10">
        {/* availability */}
        <section className="rounded-[1.4rem] bg-warmdark text-ivory p-6 sm:p-7 mb-10" data-testid="availability-panel">
          <div className="font-mono-tech text-[11px] tracking-widest uppercase text-honey mb-3">Availability status</div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => saveAvail({ ...avail, open: !avail.open })}
                data-testid="availability-toggle"
                aria-pressed={avail.open}
                className={`relative w-14 h-8 rounded-full transition-colors ${avail.open ? "bg-sage" : "bg-ivory/25"}`}
                aria-label="Toggle availability"
              >
                <span className={`absolute top-1 w-6 h-6 rounded-full bg-ivory transition-all ${avail.open ? "left-7" : "left-1"}`} />
              </button>
              <span className="text-ivory">{avail.open ? "Open to new roles" : "Not currently looking"}</span>
            </div>
            <input
              value={avail.message}
              onChange={(e) => setAvail({ ...avail, message: e.target.value })}
              onBlur={() => saveAvail(avail)}
              data-testid="availability-message"
              className="rounded-xl bg-ivory/95 text-caramel px-4 py-2.5 outline-none sm:w-72"
              placeholder="Status message shown on site"
            />
          </div>
        </section>

        {/* Recruiter Chat Analytics */}
        {analytics && (
          <section className="rounded-[1.4rem] bg-linen border border-border p-6 sm:p-7 mb-10" data-testid="analytics-panel">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle size={16} className="text-terracotta" />
              <span className="font-mono-tech text-[11px] tracking-widest uppercase text-terracotta">Recruiter chat · analytics</span>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem(TOKEN_KEY);
                    const res = await fetch(`${API}/admin/ask-analytics.csv`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!res.ok) throw new Error(`${res.status}`);
                    const blob = await res.blob();
                    const cd = res.headers.get("Content-Disposition") || "";
                    const match = cd.match(/filename="([^"]+)"/);
                    const filename = match ? match[1] : `recruiter-questions-${new Date().toISOString().slice(0,10)}.csv`;
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = filename;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("CSV downloaded.");
                  } catch (e) {
                    toast.error(errMsg(e, "Could not download the CSV."));
                  }
                }}
                data-testid="analytics-csv"
                data-cosmic="true"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-brass/40 bg-ivory text-caramel px-3 py-1 text-[11px] font-mono-tech tracking-widest uppercase hover:bg-terracotta hover:text-ivory hover:border-terracotta transition-colors"
              >
                <Download size={11} /> Export CSV
              </button>
            </div>
            <h3 className="font-display text-2xl text-caramel font-light">What recruiters ask most</h3>
            <p className="text-warmmuted text-sm mb-5 mt-1">
              Read-only insights from your AI chat widget. Use these to tune the copy on your site — there's nothing to action here.
            </p>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="rounded-2xl bg-ivory border border-border p-4">
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted">Total questions</div>
                <div className="font-display text-3xl text-terracotta leading-none mt-1" data-testid="analytics-total">{analytics.total}</div>
                <div className="text-[11px] text-warmmuted mt-2 leading-snug">Every message a recruiter has sent to your chat widget.</div>
              </div>
              <div className="rounded-2xl bg-ivory border border-border p-4">
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted">Follow-ups</div>
                <div className="font-display text-3xl text-terracotta leading-none mt-1">{analytics.followups}</div>
                <div className="text-[11px] text-warmmuted mt-2 leading-snug">2nd+ questions in the same session — a signal recruiters are engaged.</div>
              </div>
              <div className="rounded-2xl bg-ivory border border-border p-4">
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted">Unique topics</div>
                <div className="font-display text-3xl text-terracotta leading-none mt-1">{analytics.unique_topics}</div>
                <div className="text-[11px] text-warmmuted mt-2 leading-snug">Distinct themes detected across all questions.</div>
              </div>
            </div>

            {analytics.by_day?.length > 0 && (() => {
              const max = Math.max(1, ...analytics.by_day.map((d) => d.count));
              return (
                <div className="mb-6">
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted mb-2 flex items-center gap-2">
                    <TrendingUp size={12} /> Last 14 days
                  </div>
                  <div className="flex items-end gap-1.5 h-24 bg-ivory rounded-xl border border-border p-3" data-testid="analytics-by-day">
                    {analytics.by_day.map((d) => (
                      <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
                        <div
                          className="w-full rounded-t bg-terracotta/80 group-hover:bg-terracotta transition-colors"
                          style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? 3 : 0 }}
                          title={`${d.date}: ${d.count}`}
                        />
                        <div className="font-mono-tech text-[8px] text-warmmuted mt-1">{d.date.slice(-2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {analytics.top_topics?.length > 0 && (
              <div className="mb-6">
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted mb-2">Top topics</div>
                <div className="flex flex-wrap gap-2" data-testid="analytics-topics">
                  {analytics.top_topics.map((t) => (
                    <span key={t.label} className="inline-flex items-center gap-2 rounded-full border border-brass/40 bg-ivory px-3 py-1.5 text-sm text-caramel">
                      {t.label}
                      <span className="font-mono-tech text-[10px] tracking-wide text-brass">×{t.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {analytics.recent?.length > 0 && (
              <div>
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted mb-2">Recent questions</div>
                <ul className="rounded-xl bg-ivory border border-border divide-y divide-border max-h-64 overflow-y-auto" data-testid="analytics-recent">
                  {analytics.recent.map((r) => (
                    <li key={`${r.created_at}-${r.question}`} className="px-4 py-2.5 text-sm text-caramel/85 flex items-start gap-3">
                      <span className="font-mono-tech text-[10px] tracking-widest text-warmmuted shrink-0 pt-1">
                        {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                      <span className="flex-1">{r.question}</span>
                      {r.is_followup && <span className="font-mono-tech text-[9px] tracking-widest uppercase text-brass shrink-0 pt-1">follow-up</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analytics.total === 0 && (
              <p className="text-warmmuted text-sm italic">
                No recruiter questions yet. When visitors chat with your AI assistant, their questions will appear here (privately) so you can tune your copy.
              </p>
            )}
          </section>
        )}

        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <h2 className="font-display text-3xl text-caramel font-light">Testimonials <span className="text-warmmuted text-xl">({items.length})</span></h2>
          {items.some((i) => !i.approved) && (
            <button
              onClick={renotifyPending}
              disabled={renotifying}
              data-testid="admin-renotify"
              className="inline-flex items-center gap-1.5 rounded-full border border-brass/40 bg-ivory text-caramel px-4 py-2 text-sm hover:bg-terracotta hover:text-ivory hover:border-terracotta transition-colors disabled:opacity-60"
            >
              <Mail size={15} /> {renotifying ? "Sending..." : "Email me the pending ones"}
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-warmmuted">Loading...</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-warmmuted">No submissions yet.</div>
        ) : (
          <div className="space-y-4" data-testid="admin-testimonial-list">
            {items.map((t) => (
              <div key={t.id} className={`rounded-2xl border p-5 sm:p-6 ${t.approved ? "border-sage/60 bg-sage/10" : "border-border bg-linen"}`} data-testid={`admin-testimonial-${t.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-caramel">{t.first_name} {t.last_name}</span>
                      {t.approved
                        ? <span className="font-mono-tech text-[10px] tracking-widest uppercase text-forest bg-sage/30 rounded-full px-2 py-0.5">Live</span>
                        : <span className="font-mono-tech text-[10px] tracking-widest uppercase text-terracotta bg-blush/50 rounded-full px-2 py-0.5">Pending</span>}
                    </div>
                    <div className="font-mono-tech text-[11px] tracking-wide uppercase text-warmmuted mt-0.5">{t.title} · {t.company} · {t.relationship}</div>
                  </div>
                  <div className="flex text-gold">{[0,1,2,3,4].map((i) => <Star key={i} size={14} fill="currentColor" />)}</div>
                </div>
                <p className="mt-3 text-caramel/85 leading-relaxed">"{t.testimonial}"</p>
                {t.context ? <p className="mt-1 text-sm text-warmmuted">Context: {t.context}</p> : null}
                <div className="mt-2 text-sm text-warmmuted flex flex-wrap gap-x-4">
                  <span>{t.work_type}</span><span>{t.email}</span>{t.linkedin ? <a href={t.linkedin} target="_blank" rel="noreferrer" className="text-terracotta hover:underline">LinkedIn</a> : null}
                </div>
                <div className="mt-4 flex gap-2">
                  {t.approved ? (
                    <button onClick={() => setApproval(t.id, false)} data-testid={`hide-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-caramel hover:border-terracotta transition-colors text-sm"><EyeOff size={15} /> Hide</button>
                  ) : (
                    <button onClick={() => setApproval(t.id, true)} data-testid={`approve-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta text-ivory px-4 py-2 hover:bg-warmdark transition-colors text-sm"><Check size={15} /> Approve & publish</button>
                  )}
                  <button onClick={() => remove(t.id)} data-testid={`delete-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-caramel hover:border-red-500 hover:text-red-600 transition-colors text-sm"><Trash2 size={15} /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
